export interface NavigationEntry {
  route: string
  state?: Record<string, unknown>
  timestamp: number
}

export class NavigationHistoryStack {
  private entries: NavigationEntry[] = []
  private cursor = 0
  private maxSize: number

  constructor(initialRoute: string, maxSize = 50) {
    this.maxSize = maxSize
    this.entries.push({
      route: initialRoute,
      timestamp: Date.now(),
    })
  }

  push(route: string, state?: Record<string, unknown>): void {
    // Truncate any forward entries beyond cursor
    this.entries.splice(this.cursor + 1)

    this.entries.push({
      route,
      state,
      timestamp: Date.now(),
    })
    this.cursor = this.entries.length - 1

    // Enforce maxSize by dropping oldest entries
    if (this.entries.length > this.maxSize) {
      const overflow = this.entries.length - this.maxSize
      this.entries.splice(0, overflow)
      this.cursor -= overflow
    }
  }

  replace(route: string, state?: Record<string, unknown>): void {
    this.entries[this.cursor] = {
      route,
      state,
      timestamp: Date.now(),
    }
  }

  back(): NavigationEntry | null {
    if (!this.canGoBack) return null
    this.cursor--
    return this.entries[this.cursor]
  }

  forward(): NavigationEntry | null {
    if (!this.canGoForward) return null
    this.cursor++
    return this.entries[this.cursor]
  }

  get canGoBack(): boolean {
    return this.cursor > 0
  }

  get canGoForward(): boolean {
    return this.cursor < this.entries.length - 1
  }

  get currentEntry(): NavigationEntry | null {
    return this.entries[this.cursor] ?? null
  }

  get length(): number {
    return this.entries.length
  }
}
